/**
 * Normalisation des statuts de tests et calcul du taux de réussite.
 *
 * Convention retenue (alignée Robot Framework / pytest) : un test SKIP / non exécuté
 * n'est ni une réussite ni un échec — il est **exclu du dénominateur** du taux.
 *   taux = passés / (total − ignorés)
 *
 * Côté Azure DevOps, ces tests remontent sous deux formes :
 *  - `outcome` d'un test result : "NotExecuted" (Robot Framework SKIP) ou "Skipped"
 *  - résumé d'un test run : le champ `notApplicableTests`
 */

export const PASSED_STATUSES = ['passed', 'success'];
export const SKIPPED_STATUSES = ['skipped', 'notexecuted', 'not_executed', 'notapplicable'];

/**
 * Valeurs de l'enum `TestOutcome` d'Azure DevOps désignant un test non exécuté.
 * Contrepartie de SKIPPED_STATUSES côté API : sert à filtrer une requête (paramètre
 * `outcomes`), qui n'accepte que les valeurs exactes de l'enum — d'où la casse stricte
 * et l'absence de "Skipped", qui n'en fait pas partie.
 */
export const SKIPPED_OUTCOMES = ['NotExecuted', 'NotApplicable'];

const norm = (status) => String(status ?? '').toLowerCase().trim();

export const isPassedStatus = (status) => PASSED_STATUSES.includes(norm(status));
export const isSkippedStatus = (status) => SKIPPED_STATUSES.includes(norm(status));

/** Tout ce qui n'est ni passé ni ignoré (failed, inconclusive, aborted, unknown…). */
export const isFailedStatus = (status) => !isPassedStatus(status) && !isSkippedStatus(status);

/** Libellé FR d'un statut, pour l'affichage. */
export const statusLabel = (status) => {
  if (isPassedStatus(status)) return 'Passé';
  if (isSkippedStatus(status)) return 'Ignoré';
  return norm(status) === 'failed' ? 'Échoué' : 'Inconclusif';
};

/**
 * Filtre de statut d'une liste de tests.
 * La valeur 'skipped' couvre à la fois "Skipped" et "NotExecuted" (ADO en renvoie les deux).
 */
export const matchesStatusFilter = (status, filter) => {
  if (!filter || filter === 'all') return true;
  if (filter === 'skipped') return isSkippedStatus(status);
  if (filter === 'passed') return isPassedStatus(status);
  if (filter === 'failed') return isFailedStatus(status);
  return norm(status) === norm(filter);
};

/** Couleur d'un point/barre de graphe selon le statut. */
export const statusChartColor = (status) => {
  if (isPassedStatus(status)) return '#10b981';
  if (isSkippedStatus(status)) return '#9ca3af';
  return '#ef4444';
};

/**
 * Taux de réussite en excluant les tests non exécutés du dénominateur.
 * @param {number} passed - nombre de tests passés
 * @param {number} total - nombre total de tests (ignorés inclus)
 * @param {number} skipped - nombre de tests ignorés / non exécutés
 * @returns {number} pourcentage 0-100 (non arrondi)
 */
export const computeSuccessRate = (passed, total, skipped = 0) => {
  const executed = Math.max(0, (Number(total) || 0) - (Number(skipped) || 0));
  if (executed <= 0) return 0;
  return ((Number(passed) || 0) / executed) * 100;
};

/**
 * Statistiques d'une liste de tests.
 * @param {Array} items
 * @param {(item: any) => string} getStatus - accesseur du statut (défaut : item.status)
 * @returns {{total:number, passed:number, failed:number, skipped:number, executed:number, successRate:number}}
 *          `successRate` est arrondi à l'entier ; `total === passed + failed + skipped`.
 */
export const computeTestStats = (items, getStatus = (item) => item?.status) => {
  const list = Array.isArray(items) ? items : [];
  let passed = 0;
  let skipped = 0;
  let failed = 0;

  for (const item of list) {
    const status = getStatus(item);
    if (isPassedStatus(status)) passed++;
    else if (isSkippedStatus(status)) skipped++;
    else failed++;
  }

  const total = list.length;
  const executed = total - skipped;
  return {
    total,
    passed,
    failed,
    skipped,
    executed,
    successRate: Math.round(computeSuccessRate(passed, total, skipped)),
  };
};

/**
 * Variation nette du nombre de tests sur une période, calculée suite par suite : pour chaque suite,
 * on compare son total à sa première et à sa dernière exécution de la période, puis on additionne.
 * Les totaux journaliers ne servent pas de base car ils varient avec les suites qui ont tourné ce jour-là.
 * Une suite vue une seule fois dans la période compte donc 0 (pas de point de comparaison).
 * @param {Array<{suites?: Record<string, number>}>} trendData - jours triés par ordre chronologique
 * @returns {{first:number, last:number, delta:number}} totaux de départ et de fin (somme des suites) et leur écart
 */
export const computeNetTestDelta = (trendData) => {
  const firstBySuite = {};
  const lastBySuite = {};
  for (const day of Array.isArray(trendData) ? trendData : []) {
    for (const [suiteName, suiteTotal] of Object.entries(day?.suites || {})) {
      if (!(suiteName in firstBySuite)) firstBySuite[suiteName] = suiteTotal;
      lastBySuite[suiteName] = suiteTotal;
    }
  }
  const sum = (bySuite) => Object.values(bySuite).reduce((a, b) => a + b, 0);
  const first = sum(firstBySuite);
  const last = sum(lastBySuite);
  return { first, last, delta: last - first };
};
